"use client";
// import customerService from '@/app/[locale]/_service/customer.service';
import { useAuthStore } from "@/app/store/use-auth-store";
import { useCartStore } from "@/entities/cart/model/use-cart-store";
import { usePlatformStore } from "@/entities/platform";
import { useRouter } from "@/i18n/navigation";
import BottomSheet from "@/shared/ui/bottom-sheet";
import { Button } from "@/shared/ui/button";
import { CField } from "@/shared/ui/cfield";
import CPhoneInput from "@/shared/ui/phone-input";
import { toaster } from "@/shared/ui/toaster";
import ConfirmModal from "@/widgets/confirm-modal";
import TrashIcon from "@/widgets/map/Delivery/icons/TrashRed";
import { Flex, Input, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { Dispatch, FormEvent, useEffect, useState } from "react";
import { useDeleteProfile } from "../../delete/api/query";
import { useUpdateUser } from "../api/query";

// ?token=givenToken&lat=41.35575483&long=69.28867764&address=myaddress&client_id=someid&client_email=okay@gmail.com&reception_phone=99801234567&room_number=1&source=5f9b0003-d40b-4413-af63-1b3c8f053727

const EditUserData = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
}) => {
  const { mutate: updateUser, isPending } = useUpdateUser();
  const { mutate: deleteProfile } = useDeleteProfile();
  const { isPorte } = usePlatformStore();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    date_of_birth: "",
  });

  const { user, setUser, logOut } = useAuthStore();
  const { CLEAR } = useCartStore();
  const t = useTranslations();
  const router = useRouter();

  useEffect(() => {
    if (user) {
      const nameParts = user.name;
      setFormData({
        name: nameParts || "",
        phone: user.phone || "",
        date_of_birth: user.date_of_birth || "",
        email: user.email || "",
      });
    }
  }, [user]);

  const { name, phone, date_of_birth, email } = formData;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    const payload = {
      phone: email ? "" : phone,
      date_of_birth: date_of_birth || null,
      name: `${name}`.trim(),
      email: email,
    };

    updateUser(
      { id: user.id, payload },
      {
        onSuccess: () => {
          setIsOpen(false);
          toaster.success({
            title: t("successfully_edited"),
          });
          setUser({ ...user, ...payload });
        },

        onError: (error) => {
          toaster.error({
            title: "Failed to update user data",
            description: error.response?.data?.Error?.message,
          });
        },
      }
    );
  };

  const onProfileDelete = () => {
    if (!user) return;

    deleteProfile(user?.id, {
      onSuccess: () => {
        router.push("/");
        logOut();
        CLEAR();
      },
    });
  };

  return (
    <BottomSheet
      contentHeight="default"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Stack height="full" px={4} gap={0}>
        <Text color={"text"} fontSize={24} fontWeight={700}>
          {t("edit_data")}
        </Text>

        <Stack gap={4} my={4} flex={1} asChild>
          <form onSubmit={onSubmit}>
            <Stack gap={4} flex={1}>
              <CField
                label={t("profile.name")}
                bgColor="background"
                color={"text"}
              >
                <Input
                  type="text"
                  className="peer"
                  placeholder=""
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      name: e.target.value,
                    }))
                  }
                />
              </CField>
              <CField
                label={t("profile.birthday")}
                bgColor="background"
                color={"text"}
              >
                <Input
                  type="date"
                  className="peer"
                  placeholder=""
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      date_of_birth: e.target.value,
                    }))
                  }
                />
              </CField>

              {user?.phone && (
                <CField readOnly>
                  <CPhoneInput
                    label={t("phone_number")}
                    value={formData.phone}
                    disabled={isPorte}
                    onChange={(e) =>
                      typeof e === "string" &&
                      setFormData((prevData) => ({
                        ...prevData,
                        phone: e,
                      }))
                    }
                  />
                </CField>
              )}

              {user?.email && (
                <CField
                  label={t("email")}
                  bgColor="background"
                  color={"text"}
                  readOnly
                >
                  <Input type="text" className="peer" value={formData.email} />
                </CField>
              )}
            </Stack>

            <Flex gap={4}>
              {!isPorte && (
                <Button
                  flex="1"
                  h="56px"
                  color="red"
                  variant="outline"
                  colorPalette="red"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  <TrashIcon color="red" />
                  {t("profile.delete_profile")}
                </Button>
              )}

              <Button
                flex="1"
                height={"56px"}
                type="submit"
                bg="primary"
                color="primary.text"
                _hover={{ bg: "primary.hover" }}
              >
                {isPending ? <Spinner /> : t("save")}
              </Button>
            </Flex>
          </form>
        </Stack>

        <ConfirmModal
          title={t("profile.delete_profile")}
          isWarning
          description={t("profile.sure_delete_profile")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() => onProfileDelete()}
          text={{ confirm: "delete", cancel: "cancel" }}
        />
      </Stack>
    </BottomSheet>
  );
};

export default EditUserData;
